import axios from 'axios';
import api from '../utils/api';
import { Client, CreateClientDTO, UpdateClientDTO } from '../types/client';

// Convert API response to client type
const convertToClientType = (data: any): Client => ({
  _id: data._id,
  ownerName: data.ownerName || '',
  companyName: data.companyName || '',
  email: data.email || '',
  phoneNumber: data.phoneNumber || '',
  businessType: data.businessType || 'Sole Trader',
  industry: data.industry || '',
  partner: data.partner || '',
  position: data.position || '',
  companyNumber: data.companyNumber,
  incorporationDate: data.incorporationDate,
  
  // Trading Address
  tradingAddress: {
    line1: data.tradingAddress?.line1 || '',
    line2: data.tradingAddress?.line2,
    city: data.tradingAddress?.city || '',
    county: data.tradingAddress?.county,
    postcode: data.tradingAddress?.postcode || '',
    country: data.tradingAddress?.country || 'United Kingdom'
  },

  // Registered Business Address - required for Limited Companies and Charities
  registeredBusinessAddress: {
    line1: data.registeredBusinessAddress?.line1 || '',
    line2: data.registeredBusinessAddress?.line2,
    city: data.registeredBusinessAddress?.city || '',
    county: data.registeredBusinessAddress?.county,
    postcode: data.registeredBusinessAddress?.postcode || ''
  },

  // Home Address (for Sole Traders)
  homeAddress: data.homeAddress ? {
    line1: data.homeAddress.line1 || '',
    line2: data.homeAddress.line2,
    city: data.homeAddress.city || '',
    county: data.homeAddress.county,
    postcode: data.homeAddress.postcode || '',
    movedInDate: data.homeAddress.movedInDate || undefined
  } : undefined,
  
  // Address History (for Sole Traders)
  addressHistory: Array.isArray(data.addressHistory) ? data.addressHistory : undefined,
  
  dateOfBirth: data.dateOfBirth ? new Date(data.dateOfBirth).toISOString() : undefined,
  yearsTrading: typeof data.yearsTrading === 'number' ? data.yearsTrading : undefined,
  monthsTrading: typeof data.monthsTrading === 'number' ? data.monthsTrading : undefined,
  representativeId: data.representativeId || '',
  representative: data.representative ? {
    _id: data.representative._id || '',
    name: data.representative.name || '',
    email: data.representative.email || ''
  } : undefined,

  status: data.status || 'draft',
  properties: Array.isArray(data.properties) 
    ? data.properties.map((p: any) => ({
        _id: p._id?.toString() || '',
        name: p.name || '',
        address: {
          line1: p.address?.line1 || '',
          line2: p.address?.line2 || '',
          city: p.address?.city || '',
          county: p.address?.county || '',
          postcode: p.address?.postcode || '',
        },
        status: p.status || 'active',
        occupancyStatus: p.occupancyStatus || 'owner-occupied',
        utilityServices: Array.isArray(p.utilityServices) ? p.utilityServices : [],
        documents: Array.isArray(p.documents) ? p.documents : [],
        notes: p.notes || ''
      }))
    : [],
  documents: Array.isArray(data.documents)
    ? data.documents.map((d: any) => ({
        _id: d._id?.toString() || '',
        name: d.name || '',
        path: d.path || '',
        type: 'file',
        category: d.category || 'GENERAL',
        client: d.client?.toString() || '',
        property: d.property?.toString(),
        url: d.url || '',
        size: d.size || 0,
        mimeType: d.mimeType || '',
        uploadedBy: d.uploadedBy?.toString() || '',
        uploadedAt: d.createdAt || d.uploadedAt || '',
        createdAt: d.createdAt || '',
        tags: Array.isArray(d.tags) ? d.tags : []
      }))
    : [],
  utilityServices: Array.isArray(data.utilityServices)
    ? data.utilityServices.map((u: any) => ({
        _id: u._id?.toString() || '',
        serviceType: u.serviceType || 'Gas',
        provider: u.provider || '',
        accountNumber: u.accountNumber || '',
        meterNumber: u.meterNumber || '',
        status: u.status || 'LIVE',
        contractStartDate: u.contractStartDate || '',
        contractEndDate: u.contractEndDate || '',
        notes: u.notes || '',
        mpanMprSpid: u.mpanMprSpid || '',
        supplier: u.supplier || '',
        currentRate: u.currentRate || '',
        eacAq: u.eacAq || '',
        ced: u.ced || ''
      }))
    : [],
  notes: data.notes || '',
  createdAt: data.createdAt ? new Date(data.createdAt).toISOString() : new Date().toISOString(),
  updatedAt: data.updatedAt ? new Date(data.updatedAt).toISOString() : new Date().toISOString(),
  // LOA fields
  loaIssuedAt: data.loaIssuedAt ? new Date(data.loaIssuedAt).toISOString() : undefined,
  loaAcceptedAt: data.loaAcceptedAt ? new Date(data.loaAcceptedAt).toISOString() : undefined,
  loaRejectedAt: data.loaRejectedAt ? new Date(data.loaRejectedAt).toISOString() : undefined,
  loaRejectionReason: data.loaRejectionReason || undefined,
  loaToken: data.loaToken || undefined,
  loaTokenExpiry: data.loaTokenExpiry ? new Date(data.loaTokenExpiry).toISOString() : undefined,
  createdBy: data.createdBy ? {
    _id: data.createdBy._id || '',
    email: data.createdBy.email || '',
    firstName: data.createdBy.firstName || '',
    lastName: data.createdBy.lastName || ''
  } : {
    _id: '',
    email: '',
    firstName: '',
    lastName: ''
  }
});

// Get all clients
export const getClients = async (): Promise<Client[]> => {
  try {
    
    const response = await api.get('/clients');
    
    const data = response.data;
    return Array.isArray(data) ? data.map(convertToClientType) : [];
  } catch (error) {
    
    throw handleApiError(error);
  }
};

// Get client by ID
export const getClient = async (id: string): Promise<Client> => {
  try {
    
    const response = await api.get(`/clients/${id}`);
    
    return convertToClientType(response.data);
  } catch (error) {
    
    throw handleApiError(error);
  }
};

// Create new client
export const createClient = async (clientData: CreateClientDTO): Promise<Client> => {
  try {
    
    const response = await api.post('/clients', clientData);
    
    return convertToClientType(response.data);
  } catch (error) {
    
    throw handleApiError(error);
  }
};

// Update client
export const updateClient = async (id: string, clientData: UpdateClientDTO): Promise<Client> => {
  try {
    
    return api.put(`/clients/${id}`, clientData)
      .then(response => {
        // If the API doesn't return the full client data, fetch it to ensure we have properties
        if (!response.data || !response.data.properties) {
          return api.get(`/clients/${id}`).then(fullResponse => {
            return convertToClientType(fullResponse.data);
          });
        }
        
        return convertToClientType(response.data);
      });
  } catch (error) {
    
    throw handleApiError(error);
  }
};

// Delete client
export const deleteClient = async (id: string): Promise<void> => {
  try {
    
    await api.delete(`/clients/${id}`);
    
  } catch (error) {
    
    throw handleApiError(error);
  }
};

// Check for existing clients
export const checkExistingClient = async (params: {
  companyNumber?: string;
  email?: string;
  phoneNumber?: string;
  ownerName?: string;
  companyName?: string;
  postcode?: string;
}): Promise<{ exists: boolean; message: string }> => {
  try {
    
    const queryString = Object.entries(params)
      .filter(([_, value]) => value) // Remove empty values
      .map(([key, value]) => `${key}=${encodeURIComponent(value!)}`)
      .join('&');
    
    const response = await api.get(`/clients/check?${queryString}`);
    
    return response.data;
  } catch (error) {
    
    throw handleApiError(error);
  }
};

// Fuzzy match for Sole Traders
export interface FuzzyMatchResult {
  clientId: string;
  companyName: string;
  ownerName: string;
  email?: string;
  phoneNumber?: string;
  postcode?: string;
  matchScore: number;
  matchReasons: string[];
}

export const fuzzyMatchSoleTraders = async (params: {
  ownerName?: string;
  companyName?: string;
  email?: string;
  phoneNumber?: string;
  postcode?: string;
  businessType: string;
}): Promise<{ matches: FuzzyMatchResult[]; message: string }> => {
  try {
    const queryString = Object.entries(params)
      .filter(([_, value]) => value) // Remove empty values
      .map(([key, value]) => `${key}=${encodeURIComponent(value!)}`)
      .join('&');
    
    const response = await api.get(`/clients/fuzzy-match?${queryString}`);
    
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Get client utility services
export const getClientUtilityServices = async (id: string): Promise<any> => {
  try {
    
    const response = await api.get(`/clients/${id}/utility-services`);
    
    return response;
  } catch (error) {
    
    throw handleApiError(error);
  }
};

// Get client properties
export const getClientProperties = async (id: string): Promise<any> => {
  try {
    
    const response = await api.get(`/clients/${id}/properties`);
    
    return response;
  } catch (error) {
    
    throw handleApiError(error);
  }
};

// Get client documents
export const getClientDocuments = async (id: string): Promise<any> => {
  try {
    
    const response = await api.get(`/clients/${id}/documents`);
    
    return response;
  } catch (error) {
    
    throw handleApiError(error);
  }
};

// Upload client document
export const uploadClientDocument = async (id: string, file: File): Promise<any> => {
  try {
    
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(`/clients/${id}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response;
  } catch (error) {
    
    throw handleApiError(error);
  }
};

// Delete client document
export const deleteClientDocument = async (clientId: string, documentId: string): Promise<void> => {
  try {
    
    await api.delete(`/clients/${clientId}/documents/${documentId}`);
    
  } catch (error) {
    
    throw handleApiError(error);
  }
};

// LOA functions
export const issueLOA = async (clientId: string): Promise<void> => {
  try {
    
    await api.post(`/loa/${clientId}/issue-loa`, {});
    
  } catch (error) {
    
    throw handleApiError(error);
  }
};

export const getLOAByToken = async (token: string): Promise<any> => {
  try {
    
    const response = await api.get(`/loa/public/${token}`);
    
    return response.data;
  } catch (error) {
    
    throw handleApiError(error);
  }
};

export const acceptLOA = async (token: string): Promise<void> => {
  try {
    
    await api.post(`/loa/public/accept/${token}`);
    
  } catch (error) {
    
    throw handleApiError(error);
  }
};

export const rejectLOA = async (token: string, reason: string): Promise<void> => {
  try {
    
    await api.post(`/loa/public/reject/${token}`, { reason });
    
  } catch (error) {
    
    throw handleApiError(error);
  }
};

// Error handling
const handleApiError = (error: unknown): Error => {
  if (axios.isAxiosError(error)) {
    // Get the error message from the response, falling back to default messages
    const errorMessage = error.response?.data?.message || 'An error occurred';

    switch (error.response?.status) {
      case 400:
        return new Error(`Invalid request: ${errorMessage}`);
      case 401:
        return new Error('Authentication required');
      case 403:
        return new Error('You do not have permission to perform this action');
      case 404:
        return new Error('Client not found');
      case 409:
        return new Error('A client with this email address already exists');
      case 422:
        return new Error(`Validation error: ${errorMessage}`);
      case 500:
        return new Error('Server error. Please try again later');
      default:
        return new Error(errorMessage);
    }
  }
  return new Error('An unexpected error occurred. Please try again');
};